import { ChevronDown } from 'lucide-react';
import type { FilterState } from '@/types/db';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { RadiusSlider } from './RadiusSlider';
import { SortSelect } from './SortSelect';
import { CountyMultiSelect } from './CountyMultiSelect';
import { RecencyRadio } from './RecencyRadio';
import { CategoryToggle } from '@/components/map/CategoryToggle';
import { RadiusOnlyToggle } from '@/components/map/RadiusOnlyToggle';
import { AMENITY_CATEGORIES, type AmenityCategory } from '@/lib/map-constants';
import { cn } from '@/lib/utils';

export interface FilterBarProps {
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  /** Number of properties matching current filters. */
  resultCount: number;
  /** Total count regardless of filters. */
  totalCount: number;
  /** All counties present in the data set (FILT-06). */
  availableCounties: string[];
  /** When `null`, the result count text shows "…" (loading). */
  loading?: boolean;
  className?: string;
}

/**
 * `<FilterBar>` — sticky control surface for radius / sort / county /
 * recency, plus an `aria-live` result-count readout.
 *
 * Anatomy (UI-SPEC § 3.7):
 *   `<GlassPanel as="section" variant="base" role="region" aria-label="Filters"
 *      className="md:sticky md:top-16 z-30 …">`
 *   children: <RadiusSlider> · <SortSelect> · <CountyMultiSelect>
 *             · <RecencyRadio> · <ResultCountText>
 *
 * The component does NOT own filter state — the parent (`useFilters` hook
 * in Plan 05-05) holds it. This component only forwards onChange handlers
 * by mutating the shared `FilterState` shape.
 *
 * Phase 06 CARRY-1 (UI-SPEC § 3.6 / § 7.1):
 * - At `<md` the children render inside a native `<details open>` element
 *   with a `<summary>` reading "Filters · {N} of {total}". User taps to
 *   collapse.
 * - At `md+` the `<summary>` is hidden via `md:[&>summary]:hidden`; the
 *   inner div renders the original horizontal layout.
 * - Sticky preserved at md+ via `md:sticky md:top-16`; DROPPED at `<md`
 *   so the (potentially expanded) bar doesn't pin on tiny viewports.
 */
export function FilterBar({
  filters,
  setFilters,
  resultCount,
  totalCount,
  availableCounties,
  loading = false,
  className,
}: FilterBarProps) {
  const update = <K extends keyof FilterState>(key: K, val: FilterState[K]) => {
    setFilters({ ...filters, [key]: val });
  };

  // Category toggles (mirrors the map): cats drive the dog-proximity gate that
  // re-weights each property's composite/Dog-Fit score server-side.
  const visibleCats = new Set<AmenityCategory>(filters.cats as AmenityCategory[]);
  const toggleCategory = (cat: AmenityCategory) => {
    const next = new Set(visibleCats);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    // Preserve canonical AMENITY_CATEGORIES order so URL stays stable.
    const ordered = AMENITY_CATEGORIES.filter((m) => next.has(m.db)).map((m) => m.db);
    update('cats', ordered);
  };

  const summaryText = loading
    ? 'Filters · …'
    : resultCount === totalCount
      ? `All filters · ${totalCount} properties`
      : `Filters · ${resultCount} of ${totalCount}`;

  return (
    <GlassPanel
      as="section"
      variant="base"
      role="region"
      aria-label="Filters"
      className={cn(
        'md:sticky md:top-16 z-30',
        'rounded-none border-x-0',
        className,
      )}
      style={{
        background: 'rgb(10 22 38 / 0.92)',
        backdropFilter: 'blur(30px) saturate(1.6)',
        WebkitBackdropFilter: 'blur(30px) saturate(1.6)',
      }}
    >
      <details
        open
        className={cn(
          'group',
          // Hide the disclosure summary at md+ — the bar is always-visible there.
          'md:[&>summary]:hidden',
        )}
      >
        <summary
          className={cn(
            'md:hidden cursor-pointer list-none px-4 py-3',
            'flex items-center justify-between gap-2',
            'text-sm font-semibold text-foam-50/80',
            'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
          )}
        >
          <span>{summaryText}</span>
          <ChevronDown
            aria-hidden
            className="w-4 h-4 transition-transform duration-150 motion-reduce:transition-none group-open:rotate-180"
          />
        </summary>

        <div className="px-6 py-3 flex items-center gap-4 flex-wrap">
          <RadiusSlider
            value={filters.radius}
            onChange={(v) => update('radius', v)}
          />
          <SortSelect
            value={{ sort: filters.sort, nearestCat: filters.nearestCat }}
            onChange={(next) =>
              setFilters({ ...filters, sort: next.sort, nearestCat: next.nearestCat })
            }
          />
          <CountyMultiSelect
            value={filters.county}
            availableCounties={availableCounties}
            onChange={(v) => update('county', v)}
          />
          <RecencyRadio
            value={filters.recency}
            onChange={(v) => update('recency', v)}
          />
          <div aria-hidden className="w-px h-6 bg-foam-50/10" />
          {/* Dog-amenity category toggles + radius-only filter (mirror the map). */}
          <div className="flex items-center gap-2 flex-wrap">
            {AMENITY_CATEGORIES.map((meta) => (
              <CategoryToggle
                key={meta.db}
                meta={meta}
                active={visibleCats.has(meta.db)}
                onToggle={() => toggleCategory(meta.db)}
              />
            ))}
            <RadiusOnlyToggle
              active={filters.inRadiusOnly}
              onToggle={() => update('inRadiusOnly', !filters.inRadiusOnly)}
            />
          </div>
          <span
            aria-live="polite"
            aria-atomic="true"
            className="ml-auto text-sm text-foam-50/80 tabular-nums"
          >
            {loading ? '…' : `${resultCount} of ${totalCount} in radius`}
          </span>
        </div>
      </details>
    </GlassPanel>
  );
}
